import { motion, useMotionValue, useSpring, useTransform, useScroll, useInView, animate } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { useEffect, useRef, useState } from "react";

const CountUp = ({ to, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      {suffix}
    </span>
  );
};

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SERVICES_STRIP = [
  "Hospital Consultancy",
  "Insurance & TPA Support",
  "Cashless Process Support",
  "Healthcare Training",
  "Healthcare Awareness",
  "Healthcare Networking",
];

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden bg-gradient-to-b from-white via-[#EAF3F8] to-[#F4FAF7] pt-[72px]"
    >
      <div className="hero-grid-lines absolute inset-0" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[#5FA832]/10 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[380px] w-[520px] rounded-full bg-amber-500/5 blur-[120px]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="font-mono-x rounded-full border border-teal-500/30 bg-[#5FA832]/10 px-4 py-1.5 text-[10px] uppercase tracking-[0.28em] text-[#5FA832] sm:text-xs">
              Madhya Pradesh · Chhattisgarh · Maharashtra
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-[#0B3C5C] sm:text-5xl lg:text-[4.4rem]">
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={lineReveal} initial="hidden" animate="show" className="block">
                Connecting Healthcare.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span custom={1} variants={lineReveal} initial="hidden" animate="show" className="text-gradient-teal block">
                Creating Solutions.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#33586F] sm:text-lg"
          >
            We are building a connected healthcare ecosystem that brings together hospitals,
            healthcare professionals, patients and healthcare-sector stakeholders through
            practical solutions, professional support and healthcare knowledge.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#enquiry"
              data-testid="hero-cta-enquire"
              className="group inline-flex items-center gap-2 rounded-full bg-[#5FA832] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F] hover:shadow-xl hover:shadow-[#5FA832]/25"
            >
              Request a Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-footprint"
              className="inline-flex items-center gap-2 rounded-full border border-[#0B3C5C]/40 px-8 py-4 text-sm font-semibold text-[#33586F] transition-all duration-300 hover:border-[#5FA832] hover:text-[#5FA832]"
            >
              Explore Our Services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.0 }}
            className="mt-10 flex flex-wrap gap-x-12 gap-y-6"
          >
            <div data-testid="hero-stat-claims">
              <p className="font-display text-3xl font-extrabold text-[#0B3C5C] sm:text-4xl">
                <CountUp to={8000} suffix="+" />
              </p>
              <p className="font-mono-x mt-1.5 text-[10px] uppercase tracking-[0.25em] text-[#4C8F2F]">
                Cashless Claims
              </p>
            </div>
            <div data-testid="hero-stat-success">
              <p className="font-display text-3xl font-extrabold text-[#0B3C5C] sm:text-4xl">
                <CountUp to={98.89} decimals={2} suffix="%" />
              </p>
              <p className="font-mono-x mt-1.5 text-[10px] uppercase tracking-[0.25em] text-amber-500">
                Success Rate
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-x-5 gap-y-2"
          >
            {SERVICES_STRIP.map((s) => (
              <span key={s} className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-[#6B8AA0]">
                {s}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: imageY }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="spotlight-frame relative"
          >
            <div className="relative overflow-hidden rounded-[2rem] border border-[#0B3C5C]/15 shadow-2xl shadow-black/50">
              <img
                src={IMAGES.hero}
                alt="Healthcare professionals in consultation"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
            </div>

            <motion.div
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="glass-panel animate-float-slow absolute -bottom-8 -left-4 rounded-2xl p-5 shadow-2xl shadow-black/40 sm:-left-10"
              data-testid="hero-floating-badge"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#5FA832]/20">
                  <ShieldCheck className="h-5 w-5 text-[#4C8F2F]" />
                </span>
                <div>
                  <p className="font-display text-sm font-bold text-[#0B3C5C]">360° Healthcare</p>
                  <p className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-[#4C8F2F]">One Ecosystem</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 border-t border-[#0B3C5C]/15 pt-3">
                <MapPin className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs text-[#33586F]">Regional focus · MP | CG | MH</span>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
